<template>
	<div class="add-balance wrapper">
		<div class="murk">
			<div class="title-header">
				<h1>
					{{ $t('wallet.addBalance') }}
					<accountInfoGroup></accountInfoGroup>
					<div class="clearfix"></div>
				</h1>
			</div>

			<div v-if="!hideError && getNotificationString(notificationList)" id="errorContainer" class="alert alert-danger text-center" role="alert">
				<div class="error-section">
					<svgIcon iconHolderClass="icon-warning" iconId="icon-warning"/>
					<div v-html="getNotificationString(notificationList)"></div>
				</div>
			</div>

			<div v-if="maxBalanceReached || !emailVerified" id="errorContainer" class="alert alert-danger text-center" role="alert">
				<svgIcon iconHolderClass="icon-warning" iconId="icon-warning"/>
				<span v-if="maxBalanceReached" class="{'blink-once': blinkMaxBalance}">
					{{ $t('wallet.max-balance-restriction',
						{
							amount: currency === 'NX' ?
								$n(maxBalanceReached) + currency:
								$n(maxBalanceReached, 'currency', currency)
						})
					}}
				</span>

				<i18n
					v-if="!emailVerified"
					path="wallet.verify-email"
					class="{'blink-once': blinkEmailUnverified}">
					<a
						place="verifyLink"
						target="_blank"
						:href="accountSettingPageUrl"
						v-t="'wallet.verify'">
					</a>
				</i18n>
			</div>

			<div class="alert mobile-warning text-center" role="warning">
				{{ $t('wallet.mobile-warning') }}
			</div>

			<div class="select-pg-wrapper">
				<h4 class="heading-normal title-add-balance" v-t="'wallet.select-payment-method'"></h4>

				<div v-if="region === 'global'">
					<h5 v-if="paymentMethodsList.Refundable" class="heading-normal text-center method-type">NX Credit</h5>
					<pgButtonGroup
					 	v-if="paymentMethodsList.Refundable"
						:paymentMethodsList="paymentMethodsList.Refundable"
						:emailVerified="!emailVerified"
						:maxBalanceReached="maxBalanceReached"
						@click-pg="handleSelectPG">
					</pgButtonGroup>

					<h5 v-if="paymentMethodsList.NonRefundable" class="heading-normal text-center method-type">NX Prepaid</h5>
					<pgButtonGroup
						:paymentMethodsList="paymentMethodsList.NonRefundable"
						:emailVerified="!emailVerified"
						:maxBalanceReached="maxBalanceReached"
						@click-pg="handleSelectPG">
					</pgButtonGroup>
				</div>

				<div v-else>
					<pgButtonGroup
						:paymentMethodsList="paymentMethodsList"
						:emailVerified="!emailVerified"
						:maxBalanceReached="maxBalanceReached"
						@click-pg="handleSelectPG">
					</pgButtonGroup>
				</div>
			</div>

			<faqWidget v-if="!$store.state.addBalanceRedirectParams"></faqWidget>
		</div>

		<selectPricepoint
			v-if="selectedPG && pricePoints"
			:pgDetails="selectedPG"
			:pricePoints="pricePoints"
			:showModal="showPricePointSelection"
			:cancelAction="closePricepointModal">
		</selectPricepoint>

		<div class="modal" :class="{ hide: !pgLoading }">
			<div id="main-spinner" class="page-spinner-wrapper main-spinner">
				<span class="spinner-circle"></span>
			</div>
		</div>
	</div>
</template>

<script>
import formSelect from '@arena/toolkit/src/components/common/form-select';
import formError from '@arena/toolkit/src/components/common/form-error';
import modal from '@arena/toolkit/src/components/common/modal';
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import selectPricepoint from './select-pricepoint';
import accountInfoGroup from './common/account-info-group';
import pgButtonGroup from './common/pg-button-group';
import { paymentMethods, paymentMethodsNX, pricePoint } from '../plugins/WalletAPI';
import faqWidget from './common/faq-widget';
import config from '../config/environment-config';
import store from '../store';

export default {
	name: 'addBalance',
	components: {
		accountInfoGroup,
		formSelect,
		formError,
		pgButtonGroup,
		faqWidget,
		modal,
		selectPricepoint,
		svgIcon,
		store
	},
	metaInfo() {
		return {
			titleTemplate: `%s | ${this.$t('wallet.add-balance')}`,
		};
	},
	props: {
		stringSelector: {
			type: String,
			default: 'commerce'
		},
	},
	data() {
		return {
			pgName: '',
			emailVerified: this.$store.state.userData.IsEmailVerified,
			accountSettingPageUrl: `${config[process.env.NODE_ENV][environment.region].account}/setting`,
			hideErrorContainer: true,
			maxBalanceReached: this.$store.state.userData.IsExceedMaxBalance,
			balance: null,
			currency: null,
			paymentMethodsList: [],
			notificationList: [],
			pricePoints: null,
			checked: true,
			selectedPG: null,
			modalParams: {
				modalTitle: '',
				modalDescription: '',
				showModal: false,
				clickButtonPrimary: null,
				primaryButtonText: '',
				primaryButtonId: '',
				secondaryButtonText: '',
				secondaryButtonId: '',
			},
			showPricePointSelection: false,
			pgLoading: false,
			hashedUserId: this.$store.state.userData.Id,
		};
	},
	methods: {
		handleSelectPG(pgDetails, amount = undefined) {
			dataLayer.push({
				event: 'pgButtonClick',
				pgButtonClicked: pgDetails.MethodName
			});

			if (!pgDetails.Partialable) {
				this.pgLoading = true;
			}

			this.pgName = pgDetails.DisplayName;

			if (pgDetails.Partialable && environment.region === 'global') {
				this.selectedPG = pgDetails;
				this.pricePoints = pgDetails.PricePoint;
				this.showPricePointSelection = true;
				return Promise.resolve();
			} else if (pgDetails.Partialable && environment.region !== 'global') {
				this.selectedPG = pgDetails;
				this.showPricePointSelection = true;
				return Promise.resolve();
			}

			const params = {
				return_url: window.location.href,
				lang: this.$i18n.locale
			};

			if (environment.region === 'global') {
				Object.assign(params, {
					method_code: pgDetails.MethodCode,
					...(this.$store.state.addBalanceRedirectParams || {}),
				});
			} else {
				Object.assign(params, {
					currency: pgDetails.Currency,
					method_name: pgDetails.MethodName,
					...(amount ? { amount } : {}), // omit if no price point selected
				});
			}

			return this.$walletAPI.checkoutToken(params)
				.then((response) => {
					this.pgLoading = false;
					const checkoutWebBaseURL = config[process.env.NODE_ENV][environment.region].checkout;
					window.location = `${checkoutWebBaseURL}?token=${encodeURI(response.data)}&goBackURL=${encodeURIComponent(window.location.href)}`;
				})
				.catch((error) => {
					this.$eventBus.$emit('hide-loading-overlay');
					if (error.response && error.response.data && error.response.data.ErrorCode === '000-500-0100') {
						// console.log('Reached max NX balance.');
						this.$walletAPI.maxBalance()
							.then(() => {
								// this.maxBalanceReached = response.data.MaxBalance[0].Amount;
							})
							.catch(() => {
								// console.log(maxBalanceError);
							});
					} else if (error.response && error.response.data && error.response.data.ErrorCode === '000-401-0001') {
						// console.log('Email not verified.');
						// this.emailUnverified = true;
					}
				});
		},
		closePricepointModal() {
			this.showPricePointSelection = false;
		},
		getNotificationString(data) {
			if (!data) return '';
			const notification = data.find(field => field.Name === 'Wallet');
			return notification.Message[this.$i18n.locale || this.$i18n.fallbackLocale];
		},
	},
	computed: {
		region() {
			return environment.region;
		},
		hideError() {
			const result = this.notificationList.find(field => field.Name === 'Wallet');
			return !result;
		},
	},
	created() {
		Promise.all([
			this.$notificationAPI.getNotificationList(),
		]).then((d) => {
			this.notificationList = d[0];
		});
		dataLayer.push({ event: 'viewedPG', userId: this.hashedUserId });
	},
	beforeRouteEnter(to, from, next) {
		let requiredCalls = null;
		const paymentMethodNXArgs = {};

		if (to.query.org_req_amount && to.query.org_req_currency) { // redirected from checkout
			store.commit('setAddBalanceRedirectParams', {
				...to.query
			});
			Object.assign(paymentMethodNXArgs, {
				...to.query,
				min_amount: to.query.org_req_amount
			});
		}

		requiredCalls = environment.region === 'global'
			? [paymentMethodsNX(paymentMethodNXArgs)]
			: [paymentMethods(), pricePoint()];

		Promise.all(requiredCalls)
			.then(([pmResponse, ppResponse]) => {
				next((vm) => {
					if (environment.region !== 'global') {
						vm.paymentMethodsList = pmResponse.data.PaymentMethod;
						vm.pricePoints = ppResponse.data.PricePoint[0];
					} else {
						vm.paymentMethodsList = pmResponse.data.Methods;
					}
				});
			})
			.catch(() => {
				// console.log(error);
			});
	},
};
</script>

<style lang="less" scoped>
	.errorContainer {
		position: relative;
		top: 0;
	}
	.form-select-wrapper {
		margin: 0 0 15px;
	}
	.title-add-balance {
		margin: 60px 0 25px;
		text-align: center;
		color: #d9d9d9;
	}
	.alert {
		border-radius: 1px;
		&.tpa-warning {
			background-color: #bfbfbf;
			color: #000;
			// This is so the other warning can show properly spaced
			margin-bottom: 1px;
		}
		&.mobile-warning {
			background-color: #bfbfbf;
			color: #000;
		}
	}
	#errorContainer {
		margin-bottom: 1px;
		&.blink-once {
			transition:all 0.5s ease-in-out;
			animation:blink normal 1.5s 1 ease-in-out;
		}
	}
	.error-section {
		display: flex;
		justify-content: center;
		> div {
			margin-bottom: -8px;
			padding-top: 2px; 
		}
	}
	h5 {
		&.method-type {
			margin: 35px 0 20px;
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/add-balance.vue